import { useEffect, useMemo, useState } from "react";

/**
 * Date-of-birth picker using three native selects (Day / Month / Year).
 * Far better mobile UX than <input type="date"> when scrolling decades.
 *
 * value : ISO YYYY-MM-DD string (or "")
 * onChange(iso) : "" while incomplete, "YYYY-MM-DD" once all three set.
 */
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function splitIso(iso) {
  if (!iso || iso.length !== 10) return { y: "", m: "", d: "" };
  const [y, m, d] = iso.split("-");
  return { y: y || "", m: m || "", d: d || "" };
}

export function DateOfBirthPicker({ value, onChange, "data-testid": testId }) {
  // Internal state so partial selections persist across re-renders before the
  // parent has a complete ISO string to bind to.
  const initial = useMemo(() => splitIso(value), [value]);
  const [parts, setParts] = useState(initial);

  // Sync down when the parent feeds us a new value (e.g. localStorage restore)
  useEffect(() => {
    if (!value) return;
    const next = splitIso(value);
    if (next.y !== parts.y || next.m !== parts.m || next.d !== parts.d) {
      setParts(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const now = new Date();
  const thisYear = now.getFullYear();
  const years = useMemo(() => {
    const arr = [];
    for (let y = thisYear; y >= thisYear - 110; y--) arr.push(String(y));
    return arr;
  }, [thisYear]);

  const daysInMonth = (y, m) => {
    if (!y || !m) return 31;
    return new Date(Number(y), Number(m), 0).getDate();
  };
  const days = useMemo(() => {
    const max = daysInMonth(parts.y, parts.m);
    return Array.from({ length: max }, (_, i) => String(i + 1).padStart(2, "0"));
  }, [parts.y, parts.m]);

  const update = (next) => {
    const merged = { ...parts, ...next };
    setParts(merged);
    if (merged.y && merged.m && merged.d) {
      onChange?.(`${merged.y}-${merged.m}-${merged.d}`);
    } else {
      onChange?.("");
    }
  };

  const selectCls =
    "h-12 px-3 rounded-xl border border-[var(--light-border)] bg-white text-[15px] text-[var(--charcoal)] transition-all focus:outline-none focus:border-[var(--lime)] focus:ring-4 focus:ring-[var(--lime)]/20 appearance-none cursor-pointer";

  return (
    <div className="grid grid-cols-3 gap-2" data-testid={testId}>
      <select
        aria-label="Day"
        className={selectCls}
        value={parts.d}
        onChange={(e) => update({ d: e.target.value })}
        data-testid={testId ? `${testId}-day` : undefined}
      >
        <option value="">Day</option>
        {days.map((d) => (
          <option key={d} value={d}>
            {Number(d)}
          </option>
        ))}
      </select>
      <select
        aria-label="Month"
        className={selectCls}
        value={parts.m}
        onChange={(e) => update({ m: e.target.value })}
        data-testid={testId ? `${testId}-month` : undefined}
      >
        <option value="">Month</option>
        {MONTHS.map((label, i) => (
          <option key={label} value={String(i + 1).padStart(2, "0")}>
            {label}
          </option>
        ))}
      </select>
      <select
        aria-label="Year"
        className={selectCls}
        value={parts.y}
        onChange={(e) => update({ y: e.target.value })}
        data-testid={testId ? `${testId}-year` : undefined}
      >
        <option value="">Year</option>
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * Compute a human age from an ISO date. Returns null if invalid or future.
 */
export function calcAge(iso) {
  if (!iso || iso.length !== 10) return null;
  const dob = new Date(iso);
  if (isNaN(dob.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
  if (age < 0 || age > 120) return null;
  return age;
}

export default DateOfBirthPicker;
